// XiaoyuClaw SkillHub + Controller API client for chat-ui.
//
// The SkillHub and Controller services use a different backend from the chat-ui
// default ruoyi-ai endpoint, so we bypass `@/utils/request` (which is bound to
// `VITE_API_URL`) and call them directly via fetch. The base URLs come from
// `VITE_SKILLHUB_URL` and `VITE_CONTROLLER_URL`, typically wired through the
// Nginx reverse proxy (`/skillhub/`, `/controller/`).
//
// Controller self-service endpoints authenticate with the same ruoyi-ai bearer
// token held in the pinia user store (`userStore.token`); the Controller
// validates it upstream via ruoyi-ai /sso/user-info. No second token is needed.

import type {
  Automation,
  AutomationListResponse,
  AutomationRunsResponse,
  CreateTurnResponse,
  InstalledSkill,
  KnowledgeBase,
  KnowledgeListResponse,
  LLMModelsResponse,
  MCPServer,
  MCPServerDetailResponse,
  MCPTestResult,
  NotificationListResponse,
  OpenClawFileListResponse,
  OpenClawFileUploadResponse,
  OpenClawSessionPreviewResponse,
  OpenClawSessionsListResponse,
  SkillAggregatedItem,
  SkillEnvelopResponse,
  SkillListItem,
  SkillPackage,
  SkillUploadResponse,
  TurnInfo,
  TurnRequestMessage,
  XiaoyuClawInstance,
} from './types';
import { useUserStore } from '@/stores';
import { getHttpStatusTip } from '@/utils/httpErrorMessage';

const SKILLHUB_URL = import.meta.env.VITE_SKILLHUB_URL || '/skillhub';
const CONTROLLER_URL = import.meta.env.VITE_CONTROLLER_URL || '/controller';

function resolveUrl(base: string, path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  if (!base)
    return normalizedPath;
  return `${base.replace(/\/$/, '')}${normalizedPath}`;
}

async function unwrap<T>(response: Response): Promise<T> {
  if (response.status === 401)
    throw new Error('unauthorized');

  let body: SkillEnvelopResponse<T> | null = null;
  try {
    body = (await response.json()) as SkillEnvelopResponse<T>;
  }
  catch {
    throw new Error(response.statusText || 'invalid response');
  }

  if (!body || !body.success)
    throw new Error(body?.error || `Request failed: ${response.statusText}`);

  return body.data as T;
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = useUserStore().token;
  if (token)
    headers.Authorization = `Bearer ${token}`;
  return headers;
}

export interface SkillListFilters {
  category?: string;
  tags?: string;
  search?: string;
  status?: string;
}

function buildQueryString(filters?: SkillListFilters): string {
  if (!filters)
    return '';
  const params = new URLSearchParams();
  if (filters.category)
    params.set('category', filters.category);
  if (filters.tags)
    params.set('tags', filters.tags);
  if (filters.search)
    params.set('search', filters.search);
  if (filters.status)
    params.set('status', filters.status);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** List skills aggregated by name (groups versions and totals). */
export async function listSkillsAggregated(
  filters?: SkillListFilters,
): Promise<SkillAggregatedItem[]> {
  const qs = buildQueryString(filters);
  const url = resolveUrl(
    SKILLHUB_URL,
    `/skills${qs}${qs ? '&' : '?'}group=true`,
  );
  // Authenticated so SkillHub can filter the catalog by the caller's YY
  // roles; anonymous requests only see skills with no role restriction.
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok)
    throw new Error(`Failed to list skills: ${res.statusText}`);
  return unwrap<SkillAggregatedItem[]>(res);
}

/** List individual skill versions (no grouping). */
export async function listSkills(
  filters?: SkillListFilters,
): Promise<SkillListItem[]> {
  const qs = buildQueryString(filters);
  const url = resolveUrl(SKILLHUB_URL, `/skills${qs}`);
  // Same as listSkillsAggregated: attach the YY bearer for role filtering.
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok)
    throw new Error(`Failed to list skills: ${res.statusText}`);
  return unwrap<SkillListItem[]>(res);
}

/** Get full metadata + instructions for a single skill package. */
export async function getSkill(skillId: string): Promise<SkillPackage> {
  const res = await fetch(
    resolveUrl(SKILLHUB_URL, `/skills/${encodeURIComponent(skillId)}`),
  );
  if (!res.ok)
    throw new Error(`Failed to get skill: ${res.statusText}`);
  return unwrap<SkillPackage>(res);
}

/**
 * Upload a `.zip` / `.tar.gz` skill package to the shared org pool. A package
 * may contain multiple skills (nested directories are fine); every discovered
 * skill is registered and reported in the response list.
 */
export async function uploadSkill(
  authorId: string,
  file: File,
): Promise<SkillUploadResponse> {
  const formData = new FormData();
  formData.append('author_id', authorId);
  formData.append('package', file);

  const res = await fetch(resolveUrl(SKILLHUB_URL, '/skills'), {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const statusTip = getHttpStatusTip(res.status);
    if (statusTip)
      throw new Error(statusTip);
    let message = `Failed to upload skill: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through with default message
    }
    throw new Error(message);
  }
  return unwrap<SkillUploadResponse>(res);
}

/**
 * Upload a `.zip` / `.tar.gz` package as **personal** skills owned by the
 * caller (ruoyi-ai bearer token). A package may contain multiple skills;
 * names come from each SKILL.md and versions fall back to a timestamp. The
 * skills return as `published`; install them into the instance with
 * {@link installSkill}.
 */
export async function uploadPersonalSkill(
  file: File,
): Promise<SkillUploadResponse> {
  const formData = new FormData();
  formData.append('package', file);

  const res = await fetch(resolveUrl(SKILLHUB_URL, '/skills/personal'), {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  if (!res.ok) {
    const statusTip = getHttpStatusTip(res.status);
    if (statusTip)
      throw new Error(statusTip);
    let message = `Failed to upload skill: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return unwrap<SkillUploadResponse>(res);
}

/** List the caller's personal-scope skills (published, pending, rejected). */
export async function listMyPersonalSkills(): Promise<SkillListItem[]> {
  const res = await fetch(resolveUrl(SKILLHUB_URL, '/skills/personal'), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Failed to list personal skills: ${res.statusText}`);
  return unwrap<SkillListItem[]>(res);
}

/** Submit one of the caller's personal skills for admin review (share). */
export async function shareSkill(
  skillId: string,
): Promise<{ skill_id: string; status: string }> {
  const res = await fetch(
    resolveUrl(SKILLHUB_URL, `/skills/${encodeURIComponent(skillId)}/share`),
    { method: 'POST', headers: authHeaders() },
  );
  if (!res.ok) {
    let message = `Failed to share skill: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return unwrap<{ skill_id: string; status: string }>(res);
}

/** Download a skill package as a binary blob. */
export async function downloadSkill(skillId: string): Promise<Blob> {
  const res = await fetch(
    resolveUrl(SKILLHUB_URL, `/skills/${encodeURIComponent(skillId)}/download`),
  );
  if (!res.ok)
    throw new Error('Failed to download skill');
  return res.blob();
}

// === Self-service skill install API (Controller) ===

/** List skills installed in the current user's OpenClaw instance. */
export async function listMySkills(): Promise<InstalledSkill[]> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me/skills'), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Failed to list installed skills: ${res.statusText}`);
  return unwrap<InstalledSkill[]>(res);
}

export interface InstallSkillResult {
  skill_id: string;
  skill_name: string;
  status: string;
}

/** Install a skill into the current user's OpenClaw instance. */
export async function installSkill(
  skillId: string,
): Promise<InstallSkillResult> {
  const res = await fetch(
    resolveUrl(
      CONTROLLER_URL,
      `/instances/me/skills/${encodeURIComponent(skillId)}/install`,
    ),
    {
      method: 'POST',
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    let message = `Failed to install skill: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return unwrap<InstallSkillResult>(res);
}

/** Remove a skill from the current user's OpenClaw instance. */
export async function uninstallSkill(skillId: string): Promise<void> {
  const res = await fetch(
    resolveUrl(
      CONTROLLER_URL,
      `/instances/me/skills/${encodeURIComponent(skillId)}`,
    ),
    {
      method: 'DELETE',
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    let message = `Failed to uninstall skill: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
}

export type { InstanceSpec, InstanceStatus, XiaoyuClawInstance } from './types';

/** Get the current user's instance (Bearer token auth). */
export async function getMyInstance(): Promise<XiaoyuClawInstance> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me'), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Failed to get instance: ${res.statusText}`);
  return unwrap<XiaoyuClawInstance>(res);
}

/** Restart the current user's instance (also starts a stopped one). */
export async function restartMyInstance(): Promise<XiaoyuClawInstance> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me/restart'), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Failed to restart instance: ${res.statusText}`);
  return unwrap<XiaoyuClawInstance>(res);
}

/**
 * Ensure the current user has a running instance — fetches it, and restarts
 * if the status is anything other than `running`.
 */
export async function ensureMyRunningInstance(): Promise<XiaoyuClawInstance> {
  const instance = await getMyInstance();
  if (instance.status === 'running')
    return instance;
  return restartMyInstance();
}

/** Ensure the current user has an instance, creating one if necessary. */
export async function ensureMyInstance(): Promise<XiaoyuClawInstance> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me/ensure'), {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    let message = `Failed to ensure instance: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return unwrap<XiaoyuClawInstance>(res);
}

/** Build the browser-reachable gateway base URL for the given instance. */
export function getGatewayBaseURL(instanceId: string): string {
  return resolveUrl(
    CONTROLLER_URL,
    `/instances/${encodeURIComponent(instanceId)}/gateway`,
  );
}

// === Enterprise platform: connectors / knowledge / automation ===

/** Legacy alias: list the connectors (MCP servers) the caller can invoke. */
export async function listMyConnectors(): Promise<MCPServer[]> {
  return listMCPServers();
}

/**
 * Upload a file into the user's instance filesystem via the Controller
 * (`POST /instances/me/files/upload`, multipart). The file lands under
 * `/home/node/.openclaw/{subdir}/<name>` inside the container, where the
 * OpenClaw agent can read it. Returns the relative path + size.
 */
export async function uploadMyFile(
  file: File,
  subdir = 'workspace',
): Promise<{ path: string; size: number }> {
  const form = new FormData();
  form.append('files', file);
  if (subdir)
    form.append('path', subdir);
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/files/upload'),
    {
      method: 'POST',
      // NOTE: do not set Content-Type — the browser sets the multipart boundary.
      headers: authHeaders(),
      body: form,
    },
  );
  if (!res.ok)
    throw new Error(getHttpStatusTip(res.status) || `Failed to upload file: ${res.statusText}`);
  const data = await unwrap<{
    uploaded: { path: string; size: number }[];
    count: number;
  }>(res);
  const entry = data?.uploaded?.[0];
  if (!entry)
    throw new Error('upload returned no file entry');
  return entry;
}

/** One configured MCP server inside the user's OpenClaw instance. */
export interface ConfiguredConnector {
  id: string;
  name: string;
  transport: string;
  status: string;
}

/** Response for the instance connector active-set endpoints. */
export interface ConnectorActiveResponse {
  /** Connector IDs (Controller catalog) that are currently active. */
  active: string[];
  /** Servers actually configured inside the OpenClaw instance (`openclaw mcp list`). */
  configured: ConfiguredConnector[];
}

/**
 * Get the connectors currently active (configured + enabled) inside the user's
 * OpenClaw instance — i.e. the ones whose tools the agent can call right now.
 */
export async function getMyConnectorsActive(): Promise<ConnectorActiveResponse> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/connectors/active'),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to load active connectors: ${res.statusText}`);
  return unwrap<ConnectorActiveResponse>(res);
}

/**
 * Set the active connector set in the user's instance. The Controller syncs the
 * requested enabled global connectors into OpenClaw (`openclaw mcp set/reload`)
 * and disables the rest. Returns the resulting active/configured state.
 */
export async function setMyConnectorsActive(
  connectorIds: string[],
): Promise<ConnectorActiveResponse> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/connectors/active'),
    {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ connector_ids: connectorIds }),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to sync connectors: ${res.statusText}`);
  return unwrap<ConnectorActiveResponse>(res);
}

/** List personal + public knowledge bases. */
export async function listMyKnowledge(): Promise<KnowledgeListResponse> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/knowledge'),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to list knowledge: ${res.statusText}`);
  return unwrap<KnowledgeListResponse>(res);
}

export interface CreateKnowledgeRequest {
  name: string;
  description?: string;
  icon?: string;
}

/** Create a personal knowledge base (stub — backend echoes the payload). */
export async function createMyKnowledge(
  data: CreateKnowledgeRequest,
): Promise<KnowledgeBase> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/knowledge'),
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to create knowledge: ${res.statusText}`);
  return unwrap<KnowledgeBase>(res);
}

/** Active knowledge bases response: the RAGFlow dataset ids the assistant may search. */
export interface ActiveKnowledgeResponse {
  /** RAGFlow dataset ids (= ruoyi `kbId`) the user has activated. */
  dataset_ids: string[];
  /** Submitted ids that were outside the user's accessible set and dropped. */
  rejected?: string[];
}

/**
 * Get the user's actively selected knowledge bases (the per-instance RAGFlow
 * dataset allowlist the ragflow MCP proxy enforces). Returns dataset ids; map
 * them back to ruoyi knowledge base ids via the accessible list if needed.
 */
export async function getMyActiveKnowledge(): Promise<ActiveKnowledgeResponse> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/knowledge/active'),
    { headers: authHeaders() },
  );
  if (!res.ok)
    throw new Error(`Failed to load active knowledge: ${res.statusText}`);
  return unwrap<ActiveKnowledgeResponse>(res);
}

/**
 * Persist the user's selected knowledge bases. The Controller verifies each
 * dataset id is within the caller's accessible set before storing it. Pass an
 * empty array to clear the selection (retrieval disabled until re-selected).
 */
export async function setMyActiveKnowledge(
  datasetIds: string[],
): Promise<ActiveKnowledgeResponse> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/knowledge/active'),
    {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ dataset_ids: datasetIds }),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to sync active knowledge: ${res.statusText}`);
  return unwrap<ActiveKnowledgeResponse>(res);
}

/** List the caller's scheduled automations. */
export async function listMyAutomations(): Promise<AutomationListResponse> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/automation'),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to list automations: ${res.statusText}`);
  return unwrap<AutomationListResponse>(res);
}

export interface CreateAutomationRequest {
  name: string;
  description?: string;
  icon?: string;
  executor: string;
  executor_id?: string;
  /** Required for assistant; optional for the script executor. */
  prompt?: string;
  /** Script executor only (required): shell command run from the project root. */
  entry_command?: string;
  /** Script executor only: optional container image override. */
  image?: string;
  /** Script executor only: per-run timeout in seconds (backend default 600). */
  timeout_seconds?: number;
  schedule_cron: string;
  timezone?: string;
  start_time?: string;
  end_time?: string;
  notify?: boolean;
}

export interface AutomationScheduleInput {
  tab: 'daily' | 'weekly' | 'monthly' | 'workday' | 'custom';
  start: string; // YYYY-MM-DD HH:mm:ss
  frequency?: number;
  frequencyUnit?: string;
}

/**
 * Convert the date-picker local datetime string (`YYYY-MM-DD HH:mm:ss`) into
 * an RFC3339 string that the Go backend can unmarshal into `time.Time`.
 * The automation form currently assumes Asia/Shanghai; the backend stores the
 * user's chosen timezone separately and applies it when registering the job.
 */
export function formatAutomationTime(localDatetime: string): string {
  if (!localDatetime)
    return '';
  const normalized = localDatetime.trim().replace(' ', 'T');
  // Local value has no timezone offset; append the fixed Asia/Shanghai offset.
  // A future iteration can derive the offset from the selected timezone.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(normalized))
    return `${normalized}+08:00`;
  return normalized;
}

/**
 * Build a cron expression from the automation schedule UI.
 * OpenClaw cron accepts standard 5-field cron expressions.
 */
export function buildAutomationCron(input: AutomationScheduleInput): string {
  const [datePart, timePart] = input.start.split(' ');
  const [hour = 9, minute = 0] = (timePart || '09:00:00').split(':').map(Number);
  const day = Number(datePart?.split('-')[2] || 1);

  switch (input.tab) {
    case 'daily':
      return `0 ${minute} ${hour} * * *`;
    case 'workday':
      return `0 ${minute} ${hour} ? * MON-FRI`;
    case 'weekly':
      return `0 ${minute} ${hour} ? * MON`;
    case 'monthly':
      return `0 ${minute} ${hour} ${day} * *`;
    case 'custom': {
      const freq = input.frequency || 1;
      const unit = input.frequencyUnit;
      if (unit === 'minute')
        return `0 0/${freq} * * * *`;
      if (unit === 'hour')
        return `0 0 0/${freq} * * *`;
      if (unit === 'day')
        return `0 ${minute} ${hour} */${freq} * *`;
      if (unit === 'week')
        return `0 ${minute} ${hour} ? * MON`;
      if (unit === 'month')
        return `0 ${minute} ${hour} 1 */${freq} *`;
      return `0 ${minute} ${hour} * * *`;
    }
  }
}

/** Create a scheduled automation backed by an Claw cron job. */
export async function createMyAutomation(
  data: CreateAutomationRequest,
): Promise<Automation> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/automation'),
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    },
  );
  if (!res.ok) {
    // Surface the backend's reason (e.g. "instance is not running") instead of
    // a bare statusText, so the UI can show why creation actually failed.
    let message = `Failed to create automation: ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error)
        message = body.error;
    }
    catch {
      // Non-JSON error body; keep the statusText-based message.
    }
    throw new Error(message);
  }
  return unwrap<Automation>(res);
}

/** Delete an automation and remove its OpenClaw cron job. */
export async function deleteMyAutomation(id: string): Promise<void> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/automation/${encodeURIComponent(id)}`),
    {
      method: 'DELETE',
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to delete automation: ${res.statusText}`);
  await unwrap<{ ok: boolean }>(res);
}

/** Pause or resume an automation. */
export async function updateMyAutomationStatus(
  id: string,
  status: 'running' | 'paused',
): Promise<void> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/automation/${encodeURIComponent(id)}/status`),
    {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ status }),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to update automation status: ${res.statusText}`);
  await unwrap<{ status: string }>(res);
}

/** Get the OpenClaw cron run history for an automation. */
export async function getMyAutomationRuns(
  id: string,
): Promise<AutomationRunsResponse> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/automation/${encodeURIComponent(id)}/runs`),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to list automation runs: ${res.statusText}`);
  return unwrap<AutomationRunsResponse>(res);
}

/**
 * Upload the script project package (zip) for a script-executor automation.
 * The zip is unpacked on the backend and becomes the project root the
 * `entry_command` runs from. Multipart field name: `package`.
 */
export async function uploadMyAutomationScript(
  id: string,
  file: File,
): Promise<void> {
  const formData = new FormData();
  formData.append('package', file);
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/automation/${encodeURIComponent(id)}/script`),
    {
      method: 'PUT',
      // NOTE: do not set Content-Type — the browser sets the multipart boundary.
      headers: authHeaders(),
      body: formData,
    },
  );
  if (!res.ok) {
    let message = `Failed to upload script package: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  await unwrap<{ ok: boolean }>(res);
}

/** Result of fetching one run artifact (download or inline preview). */
export interface AutomationArtifactContent {
  blob: Blob;
  contentType: string;
}

/**
 * Fetch one artifact of a script-automation run. `idx` is the artifact's index
 * in the run's `artifacts` array. Reports come back inline, files as
 * attachments; the caller decides between blob-download and text preview.
 */
export async function fetchMyAutomationRunArtifact(
  automationId: string,
  runId: string,
  idx: number,
): Promise<AutomationArtifactContent> {
  const res = await fetch(
    resolveUrl(
      CONTROLLER_URL,
      `/instances/me/automation/${encodeURIComponent(automationId)}/runs/${encodeURIComponent(runId)}/artifacts/${idx}`,
    ),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    let message = `Failed to load artifact: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return {
    blob: await res.blob(),
    contentType: res.headers.get('Content-Type') || '',
  };
}

// === XiaoyuClaw self-service notifications (Controller) ===

/** List the caller's XiaoyuClaw notifications (newest first, backend-defined). */
export async function listMyNotifications(
  limit = 20,
): Promise<NotificationListResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/notifications?${params.toString()}`),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to list notifications: ${res.statusText}`);
  return unwrap<NotificationListResponse>(res);
}

/** Unread count of the caller's XiaoyuClaw notifications. */
export async function getMyUnreadNotificationCount(): Promise<number> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/notifications/unread-count'),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to get unread notification count: ${res.statusText}`);
  const data = await unwrap<{ count: number }>(res);
  return Number(data?.count ?? 0);
}

/** Mark specific XiaoyuClaw notifications as read. */
export async function markMyNotificationsRead(ids: string[]): Promise<void> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/notifications/mark-read'),
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ ids }),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to mark notifications read: ${res.statusText}`);
  await unwrap<{ ok: boolean }>(res);
}

/** Mark all of the caller's XiaoyuClaw notifications as read. */
export async function markAllMyNotificationsRead(): Promise<void> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/notifications/mark-all-read'),
    {
      method: 'POST',
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to mark all notifications read: ${res.statusText}`);
  await unwrap<{ ok: boolean }>(res);
}

// === MCP Server API (Controller) ===

/** List global MCP servers available to the current user. */
export async function listMCPServers(): Promise<MCPServer[]> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/connectors'), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Failed to list MCP servers: ${res.statusText}`);
  const body = await unwrap<{
    connectors: MCPServer[];
    authorized: number;
    total: number;
  }>(res);
  return body.connectors || [];
}

/** Get a single global MCP server with its tools. */
export async function getMCPServer(
  id: string,
): Promise<MCPServerDetailResponse> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/connectors/${encodeURIComponent(id)}`),
    { headers: authHeaders() },
  );
  if (!res.ok)
    throw new Error(`Failed to get MCP server: ${res.statusText}`);
  return unwrap<MCPServerDetailResponse>(res);
}

/** Test connectivity of a global MCP server. */
export async function testMCPServer(id: string): Promise<MCPTestResult> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/connectors/${encodeURIComponent(id)}/test`),
    {
      method: 'POST',
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    let message = `Failed to test MCP server: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return unwrap<MCPTestResult>(res);
}

/**
 * Extract plain text from an OpenClaw message content payload.
 * OpenClaw stores content as either a string or an array of parts
 * (e.g. `{ type: 'text', text: '...' }`).
 */
export function extractTextFromOpenClawContent(content: any): string {
  if (typeof content === 'string')
    return content;
  if (Array.isArray(content)) {
    return content
      .filter(
        (part: any) =>
          part && part.type === 'text' && typeof part.text === 'string',
      )
      .map((part: any) => part.text)
      .join('');
  }
  if (
    content
    && typeof content === 'object'
    && typeof content.text === 'string'
  ) {
    return content.text;
  }
  return '';
}

/**
 * Extracts the latest user query from an OpenClaw context-injection message.
 * OpenClaw wraps the real user input inside `[Current message - respond to this]`.
 */
export function extractCurrentUserQueryFromContext(
  content: any,
): string | null {
  const text = extractTextFromOpenClawContent(content);
  const marker = '[Current message - respond to this]';
  const idx = text.indexOf(marker);
  if (idx === -1)
    return null;
  const current = text.slice(idx + marker.length).trim();
  if (!current.startsWith('User:'))
    return null;
  return current.slice('User:'.length).trim();
}

// === OpenClaw session history (Controller) ===

/** List the caller's OpenClaw conversation sessions. */
export async function listMySessions(
  agentId = 'default',
  limit = 20,
  offset = 0,
): Promise<OpenClawSessionsListResponse> {
  const params = new URLSearchParams();
  params.set('agentId', agentId);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/sessions?${params.toString()}`),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to list sessions: ${res.statusText}`);
  return unwrap<OpenClawSessionsListResponse>(res);
}

/**
 * Fetch a preview of a single OpenClaw session (messages). Returns the latest
 * `limit` messages; pass the previous response's `nextCursor` as `cursor` to
 * page backwards ("older messages"). `hasMore`/`nextCursor` on the response
 * indicate whether further older pages exist.
 */
export async function getSessionPreview(
  sessionKey: string,
  limit = 50,
  cursor?: string,
): Promise<OpenClawSessionPreviewResponse> {
  const encoded = encodeURIComponent(sessionKey);
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor)
    params.set('cursor', cursor);
  const res = await fetch(
    resolveUrl(
      CONTROLLER_URL,
      `/instances/me/sessions/${encoded}/preview?${params.toString()}`,
    ),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to preview session: ${res.statusText}`);
  return unwrap<OpenClawSessionPreviewResponse>(res);
}

// === Server-side chat turns (Controller) ===

/** Outcome of {@link createMyTurn}: a fresh turn, or adoption of an active one. */
export interface CreateMyTurnResult {
  turn: CreateTurnResponse;
  /**
   * True when the Controller answered 409 (a turn is already active for this
   * session) and `turn` is that pre-existing turn — the caller should subscribe
   * to it instead of treating the conflict as an error.
   */
  adopted: boolean;
}

/**
 * Create a server-side chat turn for the given session. The Controller holds
 * the upstream connection to the OpenClaw instance; the browser only subscribes
 * to the turn's event stream (see {@link myTurnEventsURL}). A 409 response
 * (same session already has an active turn) resolves to `adopted: true` with
 * the existing turn id rather than an error.
 */
export async function createMyTurn(
  sessionKey: string,
  messages: TurnRequestMessage[],
  model?: string,
): Promise<CreateMyTurnResult> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/instances/me/turns'), {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ sessionKey, model, messages }),
  });
  if (res.status === 409) {
    // Another turn is already running on this session (double send, second
    // tab, or a retried create whose first attempt actually landed) — adopt it.
    let body: SkillEnvelopResponse<CreateTurnResponse> | null = null;
    try {
      body = (await res.json()) as SkillEnvelopResponse<CreateTurnResponse>;
    }
    catch {
      // fall through to the error below
    }
    const turn = body?.data;
    if (turn?.turnId)
      return { turn, adopted: true };
    throw new Error(body?.error || 'turn already active');
  }
  if (!res.ok) {
    let message = `Failed to create turn: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return { turn: await unwrap<CreateTurnResponse>(res), adopted: false };
}

/**
 * Probe the caller's active (running) turn for a session. Returns `null` when
 * no turn is running — e.g. after a reload, a non-null result means the turn
 * output can be re-attached to via {@link myTurnEventsURL}.
 */
export async function getMyActiveTurn(sessionKey: string): Promise<TurnInfo | null> {
  const params = new URLSearchParams();
  params.set('sessionKey', sessionKey);
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/turns/active?${params.toString()}`),
    { headers: authHeaders() },
  );
  if (!res.ok)
    throw new Error(`Failed to get active turn: ${res.statusText}`);
  return unwrap<TurnInfo | null>(res);
}

/**
 * Ask the Controller to cancel a running turn (real server-side cancellation —
 * the upstream generation is aborted, not just the browser view). Idempotent.
 */
export async function cancelMyTurn(turnId: string): Promise<TurnInfo> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/turns/${encodeURIComponent(turnId)}/cancel`),
    { method: 'POST', headers: authHeaders() },
  );
  if (!res.ok)
    throw new Error(`Failed to cancel turn: ${res.statusText}`);
  return unwrap<TurnInfo>(res);
}

/**
 * Full URL of a turn's SSE event stream for the component to `fetch` directly
 * (the stream needs a ReadableStream reader, so it cannot go through `unwrap`).
 * The caller must attach the YY bearer Authorization header itself. `cursor` is
 * the last delta sequence number already consumed — deltas with `seq > cursor`
 * are replayed before the live tail, making reconnects gap-free.
 */
export function myTurnEventsURL(turnId: string, cursor = 0): string {
  return resolveUrl(
    CONTROLLER_URL,
    `/instances/me/turns/${encodeURIComponent(turnId)}/events?cursor=${cursor}`,
  );
}

/**
 * Fetch the model catalog for the chat composer's model picker: every model of
 * the admin-configured (and enabled) LLM providers, as "<slug>/<model_id>"
 * refs. Exposes no base URLs or credentials. Deployments without configured
 * providers fall back to a single legacy "openclaw/default" entry.
 */
export async function getMyModels(): Promise<LLMModelsResponse> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/llm/models'), {
    headers: authHeaders(),
  });
  if (!res.ok)
    throw new Error(`Failed to load models: ${res.statusText}`);
  return unwrap<LLMModelsResponse>(res);
}

/**
 * Switch the caller's effective model. OpenClaw's chat endpoint routes by
 * agent, so this rewrites the instance's agent model config server-side
 * (hot-reloaded) rather than riding on the per-turn request body.
 */
export async function setMyModel(model: string): Promise<void> {
  const res = await fetch(resolveUrl(CONTROLLER_URL, '/llm/models/current'), {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ model }),
  });
  if (!res.ok) {
    let message = `Failed to switch model: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
}

/** List files in the current user's OpenClaw workspace. */
export async function listMyFiles(
  path = '',
): Promise<OpenClawFileListResponse> {
  const params = new URLSearchParams();
  if (path)
    params.set('path', path);
  const qs = params.toString();
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/files${qs ? `?${qs}` : ''}`),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(`Failed to list files: ${res.statusText}`);
  return unwrap<OpenClawFileListResponse>(res);
}

/** Upload one or more files to the current user's OpenClaw workspace. */
export async function uploadMyFiles(
  path: string,
  files: File[],
): Promise<OpenClawFileUploadResponse> {
  const formData = new FormData();
  if (path)
    formData.append('path', path);
  files.forEach((file) => {
    formData.append('files', file);
  });
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/files/upload'),
    {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    },
  );
  if (!res.ok) {
    const statusTip = getHttpStatusTip(res.status);
    if (statusTip)
      throw new Error(statusTip);
    let message = `Failed to upload files: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return unwrap<OpenClawFileUploadResponse>(res);
}

/** Create a folder in the current user's OpenClaw workspace. */
export async function createMyFolder(path: string): Promise<{ path: string }> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/files/mkdir'),
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ path }),
    },
  );
  if (!res.ok) {
    let message = `Failed to create folder: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return unwrap<{ path: string }>(res);
}

/** Download a file (or folder as a ZIP) from the current user's OpenClaw workspace. */
export async function downloadMyFile(path: string): Promise<Blob> {
  const params = new URLSearchParams();
  params.set('path', path);
  const res = await fetch(
    resolveUrl(
      CONTROLLER_URL,
      `/instances/me/files/download?${params.toString()}`,
    ),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    let message = `Failed to download file: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return res.blob();
}

/** Delete a file or directory from the current user's OpenClaw workspace. */
export async function deleteMyFile(path: string): Promise<void> {
  const params = new URLSearchParams();
  params.set('path', path);
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, `/instances/me/files?${params.toString()}`),
    {
      method: 'DELETE',
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    let message = `Failed to delete file: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
}

/** Rename/move a file or directory inside the current user's OpenClaw workspace. */
export async function renameMyFile(
  oldPath: string,
  newPath: string,
): Promise<{ old_path: string; new_path: string }> {
  const res = await fetch(
    resolveUrl(CONTROLLER_URL, '/instances/me/files/rename'),
    {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ old_path: oldPath, new_path: newPath }),
    },
  );
  if (!res.ok) {
    let message = `Failed to rename file: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return unwrap<{ old_path: string; new_path: string }>(res);
}

/** Preview a text-like file from the current user's OpenClaw workspace. */
export async function previewMyFile(path: string): Promise<string> {
  const params = new URLSearchParams();
  params.set('path', path);
  const res = await fetch(
    resolveUrl(
      CONTROLLER_URL,
      `/instances/me/files/download?${params.toString()}`,
    ),
    {
      headers: authHeaders(),
    },
  );
  if (!res.ok) {
    let message = `Failed to preview file: ${res.statusText}`;
    try {
      const body = await res.json();
      message = body?.error || message;
    }
    catch {
      // fall through
    }
    throw new Error(message);
  }
  return res.text();
}

export const skillhubApi = {
  listSkillsAggregated,
  listSkills,
  getSkill,
  uploadSkill,
  uploadPersonalSkill,
  listMyPersonalSkills,
  shareSkill,
  downloadSkill,
  listMySkills,
  installSkill,
  uninstallSkill,
  getMyInstance,
  restartMyInstance,
  ensureMyRunningInstance,
  ensureMyInstance,
  getGatewayBaseURL,
  listMyConnectors,
  listMyKnowledge,
  createMyKnowledge,
  listMyAutomations,
  createMyAutomation,
  buildAutomationCron,
  deleteMyAutomation,
  updateMyAutomationStatus,
  getMyAutomationRuns,
  uploadMyAutomationScript,
  fetchMyAutomationRunArtifact,
  listMyNotifications,
  getMyUnreadNotificationCount,
  markMyNotificationsRead,
  markAllMyNotificationsRead,
  listMCPServers,
  getMCPServer,
  testMCPServer,
  listMySessions,
  getSessionPreview,
  createMyTurn,
  getMyActiveTurn,
  cancelMyTurn,
  myTurnEventsURL,
  listMyFiles,
  uploadMyFiles,
  createMyFolder,
  downloadMyFile,
  deleteMyFile,
  renameMyFile,
  previewMyFile,
};
